import { html } from 'lit';
import { sectionHeading } from '../components/sectionHeading.js';
import type { LandingContent } from '../../shared/content/landingContent.js';
import { editorSemanticText } from '../editor/editorSemanticText.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';

export const laborShortageSection = (content: LandingContent, editor?: LandingEditorRenderOptions) => html`
  <section
    id="fachkraeftemangel"
    class=${editorNodeClass(editor, 'section.laborShortage', 'vp-section vp-labor-section')}
    style=${editorNodeStyle(editor, 'section.laborShortage')}
    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'section.laborShortage', event)}
    @click=${(event: MouseEvent) => editorNodeClick(editor, 'section.laborShortage', event)}
  >
    <div class="vp-shell vp-labor-layout">
      <div>
        ${sectionHeading({
          eyebrow: content.laborShortage.eyebrow,
          title: content.laborShortage.title,
          highlight: content.laborShortage.titleHighlight,
          text: content.laborShortage.text,
          editor,
          elementPrefix: 'laborShortage',
        })}
        <div
          class=${editorNodeClass(editor, 'laborShortage.points', 'vp-labor-points')}
          style=${editorNodeStyle(editor, 'laborShortage.points')}
          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'laborShortage.points', event)}
          @click=${(event: MouseEvent) => editorNodeClick(editor, 'laborShortage.points', event)}
        >
          ${content.laborShortage.points.map((point, index) => {
            const pointId = `laborShortage.point.${index}`;
            return html`
              <article
                class=${editorNodeClass(editor, pointId, 'vp-labor-point')}
                style=${editorNodeStyle(editor, pointId)}
                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, pointId, event)}
                @click=${(event: MouseEvent) => editorNodeClick(editor, pointId, event)}
              >
                <span
                  class=${editorNodeClass(editor, `${pointId}.icon`, point.iconClass)}
                  style=${editorNodeStyle(editor, `${pointId}.icon`)}
                  @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${pointId}.icon`, event)}
                  @click=${(event: MouseEvent) => editorNodeClick(editor, `${pointId}.icon`, event)}
                ></span>
                <div>
                  ${editorSemanticText({
                    editor,
                    elementId: `${pointId}.title`,
                    fallbackTag: 'h3',
                    content: point.title,
                  })}
                  ${editorSemanticText({ editor, elementId: `${pointId}.text`, fallbackTag: 'p', content: point.text })}
                </div>
              </article>
            `;
          })}
        </div>
      </div>
      <aside
        class=${editorNodeClass(editor, 'laborShortage.stats', 'vp-labor-stat-panel reveal-card')}
        style=${editorNodeStyle(editor, 'laborShortage.stats')}
        @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'laborShortage.stats', event)}
        @click=${(event: MouseEvent) => editorNodeClick(editor, 'laborShortage.stats', event)}
      >
        ${content.laborShortage.stats.map((stat, index) => {
          const statId = `laborShortage.stat.${index}`;
          return html`
            <article
              class=${editorNodeClass(editor, statId, 'vp-labor-stat')}
              style=${editorNodeStyle(editor, statId)}
              @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, statId, event)}
              @click=${(event: MouseEvent) => editorNodeClick(editor, statId, event)}
            >
              <strong
                class=${editorNodeClass(editor, `${statId}.value`)}
                style=${editorNodeStyle(editor, `${statId}.value`)}
                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${statId}.value`, event)}
                @click=${(event: MouseEvent) => editorNodeClick(editor, `${statId}.value`, event)}
              >
                ${stat.value}
              </strong>
              <p
                class=${editorNodeClass(editor, `${statId}.label`)}
                style=${editorNodeStyle(editor, `${statId}.label`)}
                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${statId}.label`, event)}
                @click=${(event: MouseEvent) => editorNodeClick(editor, `${statId}.label`, event)}
              >
                ${stat.label}
              </p>
              <a
                class=${editorNodeClass(editor, `${statId}.sourceLabel`, 'vp-labor-source-link')}
                style=${editorNodeStyle(editor, `${statId}.sourceLabel`)}
                href=${stat.sourceHref}
                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${statId}.sourceLabel`, event)}
                @click=${(event: MouseEvent) => editorNodeClick(editor, `${statId}.sourceLabel`, event)}
              >
                ${stat.sourceLabel}
              </a>
            </article>
          `;
        })}
        <div
          class=${editorNodeClass(editor, 'laborShortage.sources', 'vp-labor-sources')}
          style=${editorNodeStyle(editor, 'laborShortage.sources')}
          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'laborShortage.sources', event)}
          @click=${(event: MouseEvent) => editorNodeClick(editor, 'laborShortage.sources', event)}
        >
          ${content.laborShortage.sources.map(
            (source, index) => html`
              <a
                class=${editorNodeClass(editor, `laborShortage.source.${index}.label`)}
                style=${editorNodeStyle(editor, `laborShortage.source.${index}.label`)}
                href=${source.href}
                @pointerdown=${(event: PointerEvent) =>
                  editorNodePointerDown(editor, `laborShortage.source.${index}.label`, event)}
                @click=${(event: MouseEvent) => editorNodeClick(editor, `laborShortage.source.${index}.label`, event)}
              >
                ${source.label}
              </a>
            `,
          )}
        </div>
      </aside>
    </div>
  </section>
`;
